const NS = 'http://www.w3.org/2000/svg';
export const ORCHARD_CHARM_ATLAS = new URL('../assets/game-orchard-charms-v1.webp', import.meta.url)
  .href;
const spriteAtlas = ORCHARD_CHARM_ATLAS;
export const ORCHARD_BURST_ATLAS = new URL(
  '../assets/game-orchard-bloom-burst-v1.webp',
  import.meta.url,
).href;
const powerAtlas = ORCHARD_BURST_ATLAS;
let serial = 0;
const silhouettes = [
  'M19 81C1 47 23 23 72 21C81 20 80 9 71 12C82 0 98 11 92 31C86 57 61 91 19 81Z',
  'M50 24C22 2 0 37 14 66C24 91 41 97 50 89C65 103 91 77 92 48C93 20 70 7 50 24Z',
  'M50 28C20-7 2 26 25 45C-8 57 9 87 35 73C32 109 71 108 68 74C100 92 108 54 76 45C101 18 68-4 50 28Z',
  'M18 42Q50 20 82 42Q86 74 50 95Q14 73 18 42ZM12 42Q10 9 50 12Q89 10 88 42Z',
  'M52 4C62 29 91 48 86 70C78 108 18 108 13 72C8 48 37 35 52 4Z',
  'M12 62C9 29 48 24 54 50C77 20 100 40 91 70C85 94 59 96 51 77C34 104 11 86 12 62Z',
];

function node(ctx, host, tag, attributes) {
  const element =
    typeof document.createElementNS === 'function' ? document.createElementNS(NS, tag) : ctx.e(tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
  host?.append(element);
  return element;
}

/** Original rounded botanical charm sprites with a native botanical fallback. Native buttons retain spoken colour and
 * shape identity; charged pieces change the whole material, not a corner badge.
 * SVG is the same on WebGL-capable and fallback devices and allocates no loop. */
export function jewelArtwork(ctx, cell, shape) {
  const art = node(ctx, null, 'svg', {
    class: 'game-piece-art game-gem-art',
    viewBox: '0 0 100 100',
    'aria-hidden': 'true',
    focusable: 'false',
    'data-symbol': cell.glyph || shape,
    'data-power': cell.special || 'ordinary',
  });
  const id = `orchard-jewel-${++serial}`;
  const material = node(ctx, art, 'g', { 'data-vector-fallback': 'true' });
  const defs = node(ctx, art, 'defs', {});
  const spriteClip = node(ctx, defs, 'clipPath', { id: `${id}-sprite-clip` });
  node(ctx, spriteClip, 'rect', { x: 0, y: 0, width: 100, height: 100 });
  const gradient = node(ctx, defs, 'linearGradient', {
    id,
    x1: '15%',
    y1: '0%',
    x2: '85%',
    y2: '100%',
  });
  for (const [offset, colour] of [
    ['0%', 'var(--game-white)'],
    ['17%', 'var(--piece-colour)'],
    ['62%', 'var(--piece-colour)'],
    ['100%', 'color-mix(in srgb, var(--piece-colour) 65%, var(--game-night))'],
  ])
    node(ctx, gradient, 'stop', { offset, 'stop-color': colour });
  node(ctx, material, 'path', {
    d: silhouettes[((cell.colour ?? 0) + 6) % 6],
    fill: `url(#${id})`,
    stroke: 'var(--game-white)',
    'stroke-width': 2.5,
    'stroke-linejoin': 'round',
  });
  node(ctx, material, 'path', {
    d: 'M25 38Q28 23 42 24',
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 5,
    'stroke-linecap': 'round',
    opacity: 0.7,
  });
  // One alpha atlas is decoded by the browser and reused by every jewel. SVG's
  // viewport clips each fixed 512px cell without cropping or changing layout.
  const colour = ((cell.colour ?? 0) + 6) % 6;
  const spriteWindow = node(ctx, art, 'g', { 'clip-path': `url(#${id}-sprite-clip)` });
  const sprite = node(ctx, spriteWindow, 'image', {
    href: spriteAtlas,
    x: -(colour % 3) * 100,
    y: -Math.floor(colour / 3) * 100,
    width: 300,
    height: 200,
    preserveAspectRatio: 'none',
    'data-jewel-sprite': colour,
  });
  sprite.addEventListener('load', () => material.setAttribute('visibility', 'hidden'));
  sprite.addEventListener('error', () => {
    material.setAttribute('visibility', 'visible');
    sprite.remove();
  });
  if (cell.special) {
    const spectrum = cell.special === 'spectrum';
    const powerFallback = node(ctx, art, 'g', { 'data-power-fallback': cell.special });
    if (spectrum) {
      const rainbow = node(ctx, defs, 'linearGradient', {
        id: `${id}-rainbow`,
        x2: '100%',
        y2: '100%',
      });
      for (let i = 0; i < 6; i++)
        node(ctx, rainbow, 'stop', {
          offset: `${i * 20}%`,
          'stop-color': `var(--game-colour-${i})`,
        });
    }
    node(ctx, powerFallback, 'circle', {
      cx: 50,
      cy: 50,
      r: spectrum ? 32 : 26,
      fill: spectrum ? `url(#${id}-rainbow)` : 'var(--piece-colour)',
      stroke: 'var(--game-white)',
      'stroke-width': 3,
    });
    node(ctx, powerFallback, 'path', {
      d: 'M50 25L56 43L76 50L56 57L50 77L44 57L24 50L44 43Z',
      fill: 'var(--game-white)',
    });
    node(ctx, powerFallback, 'ellipse', {
      cx: 50,
      cy: 50,
      rx: 45,
      ry: 19,
      transform: 'rotate(-35 50 50)',
      fill: 'none',
      stroke: 'var(--game-white)',
      'stroke-width': 2,
    });
    // Bloom is a smaller inset of the first rendered bud cell. Its clip must end at
    // that cell's right edge so the neighbouring animation frames cannot leak through.
    const powerClip = node(ctx, defs, 'clipPath', { id: `${id}-power-clip` });
    node(ctx, powerClip, 'rect', {
      x: spectrum ? 0 : 15,
      y: spectrum ? 0 : 15,
      width: spectrum ? 100 : 70,
      height: spectrum ? 100 : 70,
    });
    const powerWindow = node(ctx, art, 'g', { 'clip-path': `url(#${id}-power-clip)` });
    const power = node(ctx, powerWindow, 'image', {
      href: powerAtlas,
      // Bloom still matches its original colour: retain that cut around its
      // golden energy core. Harvest has no own-colour matching requirement.
      x: spectrum ? -200 : 15,
      y: spectrum ? 0 : 15,
      width: spectrum ? 300 : 210,
      height: spectrum ? 300 : 210,
      preserveAspectRatio: 'none',
      'data-power-sprite': cell.special,
    });
    power.addEventListener('load', () => {
      powerFallback.setAttribute('visibility', 'hidden');
      spriteWindow.setAttribute('visibility', spectrum ? 'hidden' : 'visible');
      material.setAttribute('visibility', 'hidden');
    });
    power.addEventListener('error', () => {
      powerFallback.setAttribute('visibility', 'visible');
      spriteWindow.setAttribute('visibility', 'visible');
      power.remove();
    });
  }
  return art;
}
